import type { ReactNode } from 'react';
import React, { memo, useCallback, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Button from '@atlaskit/button/button';
import IconButton from '@atlaskit/button/icon/button';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import CrossIcon from '@atlaskit/icon/core/cross';
import { Box, Flex, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { SidePanel, SidePanelBody, SidePanelFooter, SidePanelHeader } from '@confluence/side-panel';

import type { RawSuggestion } from '../__types__/SpaceSuggestion.ts';

import { SuggestionReviewRow } from './SuggestionReviewRow.tsx';

const i18n = defineMessages({
	title: {
		id: 'space-suggestions.review-panel.title',
		defaultMessage: 'Review suggestion',
		description: 'Title for the suggestion review panel',
	},
	close: {
		id: 'space-suggestions.review-panel.close-label',
		defaultMessage: 'Close',
		description: 'Accessibility label for the close button on the suggestion review panel',
	},
	cancel: {
		id: 'space-suggestions.review-panel.cancel-button',
		defaultMessage: 'Cancel',
		description: 'Label for the cancel button on the suggestion review panel',
	},
	approve: {
		id: 'space-suggestions.review-panel.approve-button',
		defaultMessage: 'Approve',
		description: 'Label for the approve button on the suggestion review panel',
	},
	subtext: {
		id: 'space-suggestions.review-panel.subtext',
		defaultMessage: '{count, plural, one {# suggested change} other {# suggested changes}}',
		description:
			'Subtext shown below the title in the suggestion review panel indicating the number of suggested changes',
	},
});

export const SUGGESTION_REVIEW_PANEL_WIDTH = 400;

export type SuggestionReviewPanelProps = {
	/** Raw backend suggestion whose actions are displayed for review. */
	rawSuggestion: RawSuggestion;
	/** Human-readable relative time string shown next to the suggested-changes count. */
	timestamp: ReactNode;
	onClose: () => void;
	onCancel: () => void;
	onApprove: () => void;
	isApproveDisabled?: boolean;
	/** Panel width in pixels. Defaults to 400. */
	width?: number;
	/** Total height of banners/headers above the panel, used to offset vertical position. */
	headerHeight?: number;
	/** Renders panel content inside an external sidebar instead of a fixed overlay. */
	isEmbedded?: boolean;
};

export const SuggestionReviewPanel = memo(
	({
		rawSuggestion,
		timestamp,
		onClose,
		onCancel,
		onApprove,
		isApproveDisabled = false,
		width = SUGGESTION_REVIEW_PANEL_WIDTH,
		headerHeight = 0,
		isEmbedded = false,
	}: SuggestionReviewPanelProps) => {
		const intl = useIntl();
		const { formatMessage } = intl;
		const reviewActions = rawSuggestion.actions;
		const actionCount = reviewActions.length;
		const [checkedActions, setCheckedActions] = useState<boolean[]>(() =>
			reviewActions.map(() => true),
		);
		const isEveryActionUnchecked = useMemo(
			() => checkedActions.every((isChecked) => !isChecked),
			[checkedActions],
		);
		const handleCheckedChange = useCallback((index: number, isChecked: boolean) => {
			setCheckedActions((currentCheckedActions) =>
				currentCheckedActions.map((currentIsChecked, currentIndex) =>
					currentIndex === index ? isChecked : currentIsChecked,
				),
			);
		}, []);
		const isApproveButtonDisabled = isApproveDisabled || isEveryActionUnchecked;

		const panelContent = (
			<>
				<SidePanelHeader>
					<Flex direction="row" justifyContent="space-between" alignItems="center">
						<Stack space="space.050">
							<Heading size="small">{formatMessage(i18n.title)}</Heading>
							<Text size="small" color="color.text.subtlest">
								{formatMessage(i18n.subtext, { count: actionCount })} • {timestamp}
							</Text>
						</Stack>
						<IconButton
							icon={CrossIcon}
							label={formatMessage(i18n.close)}
							appearance="subtle"
							onClick={onClose}
						/>
					</Flex>
				</SidePanelHeader>

				<SidePanelBody>
					<Stack space="space.100">
						{reviewActions.map((action, index) => (
							<SuggestionReviewRow
								key={`${rawSuggestion.id}-${index}`}
								action={action}
								index={index}
								isChecked={checkedActions[index] ?? true}
								onCheckedChange={handleCheckedChange}
							/>
						))}
					</Stack>
				</SidePanelBody>

				<SidePanelFooter>
					<Flex direction="row" gap="space.100" justifyContent="end">
						<Button onClick={onCancel}>{formatMessage(i18n.cancel)}</Button>
						<Button appearance="primary" onClick={onApprove} isDisabled={isApproveButtonDisabled}>
							{formatMessage(i18n.approve)}
						</Button>
					</Flex>
				</SidePanelFooter>
			</>
		);

		if (isEmbedded) {
			return <Box xcss={styles.embeddedPanel}>{panelContent}</Box>;
		}

		return (
			<SidePanel isFixed width={width} headerHeight={headerHeight} panelTop={headerHeight}>
				{panelContent}
			</SidePanel>
		);
	},
);

const styles = cssMap({
	embeddedPanel: {
		backgroundColor: token('elevation.surface'),
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		width: '100%',
	},
});
